import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { COLORS } from "@/constants/colors";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { OrdinalRange } from "@/types/common";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { getColorScaleMap } from "@/utils/color";
import { generateColorRanges, isValidPointsData } from "@/utils/common";
import {
  calculateFeaturePropertyColorRanges,
  hasCustomOrdinalAssignments,
  isOrdinalType
} from "@/utils/dataset";
import { ensureMapStyleReady } from "@/utils/map";
import {
  addClusterCustomBreaksLayers,
  addClusterCustomOrdinalLayers,
  addClusterOrdinalLayers,
  addClusterQuantileLayers,
  addClusterSimpleLayers,
  addClusterSource,
  dedupePoiFeatures
} from "@/utils/map/point";

type UsePointLayersProps = {
  map: Map;
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
  }) => Promise<void>;
};

export const usePointLayers = ({
  map,
  handleFeatureClick,
  handleMovePositionToLayer,
  handleUpdateSourceWithRank
}: UsePointLayersProps) => {
  const dispatch = useDispatch();
  const {
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    isCustomMapVisualizationLegendEnabled
  } = useAppFeatures();

  const handleAddPointsLayer = (
    dataset: Dataset,
    pointsData: FeatureCollection<Geometry>,
    customId?: string,
    filteredData?: FeatureCollection<Geometry>,
    beforeId?: string
  ) => {
    if (!isValidPointsData(map, pointsData) || !map) {
      return;
    }
    // Defer until the map's style is ready, so getSource() below is safe.
    const retry = () =>
      handleAddPointsLayer(
        dataset,
        pointsData,
        customId,
        filteredData,
        beforeId
      );
    if (!ensureMapStyleReady(map, retry)) return;
    const sourceId = customId || dataset.data.id;
    if (map.getSource(sourceId)) return;

    const newLayer = {
      id: sourceId,
      type: dataset.properties.spatialType,
      sublayers: {
        circle: `${dataset.data.id}-circle`,
        count: `${dataset.data.id}-count`,
        unclustered: `${dataset.data.id}-unclustered-points`,
        icon: `${dataset.data.id}-icon`,
        label: `${dataset.data.id}-label`,
        rank: `${dataset.data.id}-rank`
      }
    };

    // Dedup by POI ID once so the source, color ranges and maxRanking all agree
    // on distinct POIs
    const dedupedPointsData: FeatureCollection<Geometry> = {
      ...pointsData,
      features: dedupePoiFeatures(pointsData.features)
    };
    const dedupedFilteredData: FeatureCollection<Geometry> | undefined =
      filteredData
        ? {
            ...filteredData,
            features: dedupePoiFeatures(filteredData.features)
          }
        : undefined;
    const sourceData = dedupedFilteredData ?? dedupedPointsData;

    addClusterSource(map, sourceId, sourceData);

    let updatedStyle: DatasetStyle;

    const isOrdinal = isOrdinalType(dataset.selectedCategory.data_type);
    const colorSteps = dataset.style.colorSteps || 10;
    const colorFamily = dataset.style.colorFamily || "TEAL";

    // Simple Color Scale (flag-gated): one flat fill/outline for every point.
    // When the flag is off, fall through to the legacy ordinal/quantile paths.
    const isSimple =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "simple";

    if (isSimple) {
      updatedStyle = {
        ...(dataset.style || {}),
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            sourceData.features.length
          )
        }
      };

      addClusterSimpleLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );

      handleUpdateSourceWithRank({
        datasetId: sourceId,
        style: updatedStyle,
        update: {}
      });

      dispatch(
        updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
      );

      handleFeatureClick(newLayer.sublayers.circle);
      handleFeatureClick(newLayer.sublayers.unclustered);
      // In image mode the dot is hidden, so the image (icon) layer carries the
      // click; register it too so image markers stay clickable.
      handleFeatureClick(newLayer.sublayers.icon);

      if (dataset.properties.isVisible) handleMovePositionToLayer(pointsData);
      return;
    }

    // Custom Ordinal (flag-gated): per-value fill/outline from the committed
    // config — a point's colors follow the category its attribute value is in.
    // Custom Ordinal only "takes effect" once values are assigned; with an empty
    // config it falls through to the default ordinal/quantile coloring below.
    const isCustomOrdinal =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_ordinal" &&
      hasCustomOrdinalAssignments(dataset.style.customOrdinalConfig);

    if (isCustomOrdinal) {
      updatedStyle = {
        ...(dataset.style || {}),
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            sourceData.features.length
          )
        }
      };

      addClusterCustomOrdinalLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );

      handleUpdateSourceWithRank({
        datasetId: sourceId,
        style: updatedStyle,
        update: {}
      });

      dispatch(
        updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
      );

      handleFeatureClick(newLayer.sublayers.circle);
      handleFeatureClick(newLayer.sublayers.unclustered);
      // In image mode the dot is hidden, so the image (icon) layer carries the
      // click; register it too so per-category image markers stay clickable.
      handleFeatureClick(newLayer.sublayers.icon);

      if (dataset.properties.isVisible) handleMovePositionToLayer(pointsData);
      return;
    }

    // Custom Breaks (flag-gated): per-range fill/outline from the committed
    // config — a point's colors follow the numeric range its value falls into.
    const isCustomBreaks =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_breaks" &&
      !!dataset.style.customBreaksConfig;

    if (isCustomBreaks) {
      updatedStyle = {
        ...(dataset.style || {}),
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            (filteredData ?? pointsData).features.length
          )
        }
      };

      addClusterCustomBreaksLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );

      handleUpdateSourceWithRank({
        datasetId: sourceId,
        style: updatedStyle,
        update: {}
      });

      dispatch(
        updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
      );

      handleFeatureClick(newLayer.sublayers.circle);
      handleFeatureClick(newLayer.sublayers.unclustered);
      handleFeatureClick(newLayer.sublayers.icon);

      if (dataset.properties.isVisible) handleMovePositionToLayer(pointsData);
      return;
    }

    const ranges = calculateFeaturePropertyColorRanges(
      dedupedPointsData, // full original data (but deduped for duplicate POI ID), not `sourceData` because we want the full range of values to determine the color ranges, even if some points are filtered out
      dataset.selectedCategory.attribute_name,
      isOrdinal,
      dataset.style.colorSteps || 10
    );

    if (isOrdinal) {
      // Ordinal color scale
      const ordinalRanges = ranges as OrdinalRange[];
      const colorRanges = ordinalRanges.map((range) => {
        if (!range.category) {
          return {
            value: "",
            color: "#D3D3D3",
            label: "null"
          };
        }
        return {
          value: range.category,
          color:
            COLORS[colorFamily][
              getColorScaleMap(colorFamily, colorSteps)[range.color]
            ],
          label: range.category
        };
      });

      updatedStyle = {
        ...(dataset.style || {}),
        colorRanges,
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            sourceData.features.length
          )
        }
      };

      addClusterOrdinalLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );
    } else {
      // Quantile scale
      const colorRanges = generateColorRanges(
        ranges,
        colorFamily,
        dataset.selectedCategory.data_type,
        colorSteps
      );

      updatedStyle = {
        ...(dataset.style || {}),
        colorRanges,
        colorFamily,
        rankConfig: {
          isEnabled: dataset.style?.rankConfig?.isEnabled ?? false,
          maxRanking: Math.min(
            dataset.style?.rankConfig?.maxRanking ?? 1,
            sourceData.features.length
          )
        }
      };

      addClusterQuantileLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name,
        beforeId
      );
    }

    handleUpdateSourceWithRank({
      datasetId: sourceId,
      style: updatedStyle,
      update: {}
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );

    handleFeatureClick(newLayer.sublayers.circle);
    handleFeatureClick(newLayer.sublayers.unclustered);

    if (dataset.properties.isVisible) handleMovePositionToLayer(pointsData);
  };

  return {
    handleAddPointsLayer
  };
};

export default usePointLayers;
